import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Building2, Wrench, Mail, ArrowLeft, Eye, EyeOff, ShieldCheck, Smartphone, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Contains a letter", test: (p) => /[A-Za-z]/.test(p) },
  { label: "Contains a number", test: (p) => /[0-9]/.test(p) },
  { label: "Contains a special character (!@#$%^&*)", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(p) },
];

export default function PortalLogin() {
  const [view, setView] = useState("login"); // login, register, forgot, registered, 2fa
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    company_name: "",
    phone: "",
  });
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirmPwd, setShowRegConfirmPwd] = useState(false);

  // 2FA state (email OTP)
  const [twoFaTempToken, setTwoFaTempToken] = useState("");
  const [twoFaMaskedEmail, setTwoFaMaskedEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resending, setResending] = useState(false);

  // Forgot-password (email OTP) state
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPwd, setForgotNewPwd] = useState("");
  const [forgotStep, setForgotStep] = useState("request"); // 'request' | 'verify'
  const [showForgotPwd, setShowForgotPwd] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);

      // Admin 2FA via EMAIL OTP — server has already emailed a 6-digit code.
      if (response.data.requires_2fa) {
        setTwoFaTempToken(response.data.temp_token);
        setTwoFaMaskedEmail(response.data.masked_email || "your registered email");
        setView("2fa");
        setOtpCode("");
        toast.success(`A 6-digit verification code has been emailed to ${response.data.masked_email || "your email"}.`, { duration: 6000 });
        setIsLoading(false);
        return;
      }

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Login successful!");

      const userType = response.data.user.user_type;
      if (userType === "admin") {
        navigate("/portal/admin");
      } else if (userType === "staff") {
        navigate("/portal/staff");
      } else {
        navigate("/portal/client");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Invalid email or password";
      if (error.response?.status === 403) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!twoFaTempToken) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/2fa/resend-otp`, { temp_token: twoFaTempToken });
      toast.success(`New code emailed to ${twoFaMaskedEmail}.`);
      setOtpCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        temp_token: twoFaTempToken,
        otp_code: otpCode
      });
      
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Login successful!");
      navigate("/portal/admin");
    } catch (error) {
      const detail = error.response?.data?.detail || "Invalid verification code";
      toast.error(detail);
      setOtpCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate password rules
    const allRulesPassed = PASSWORD_RULES.every((r) => r.test(registerData.password));
    if (!allRulesPassed) {
      toast.error("Password does not meet all requirements");
      return;
    }
    
    // Validate passwords match
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/register`, {
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        company_name: registerData.company_name,
        phone: registerData.phone,
      });
      setView("registered");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Email may already be registered.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotStep("verify");
      toast.success(`If an account exists with ${forgotEmail}, a 6-digit OTP has been emailed. Check your inbox.`, { duration: 6000 });
    } catch {
      // Always proceed to OTP step — don't reveal whether email is registered
      setForgotStep("verify");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordWithOtp = async (e) => {
    e.preventDefault();
    if (forgotOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP from your email");
      return;
    }
    const failedRules = PASSWORD_RULES.filter((r) => !r.test(forgotNewPwd));
    if (failedRules.length > 0) {
      toast.error("New password does not meet all requirements");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-otp`, {
        email: forgotEmail,
        otp_code: forgotOtp,
        new_password: forgotNewPwd,
      });
      toast.success("Password reset successfully. Please log in with your new password.");
      setView("login");
      setForgotStep("request");
      setForgotEmail("");
      setForgotOtp("");
      setForgotNewPwd("");
      setForgotSubmitted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="portal-login-page">
      {/* Hero */}
      <section className="py-16 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Client Portal
            </h1>
            <p className="text-lg text-[#94A3B8]">
              Access shade cards, approve production drawings, and manage your projects
            </p>
          </motion.div>
        </div>
      </section>

      {/* Login Types Info - Only Client and Staff */}
      <section className="py-8 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6 text-center">
            <div className="p-4">
              <Building2 className="w-8 h-8 text-[#F97316] mx-auto mb-2" />
              <h3 className="font-semibold text-[#0F172A]">Client Login</h3>
              <p className="text-sm text-[#64748B]">View & approve drawings, select material shades</p>
            </div>
            <div className="p-4">
              <Wrench className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
              <h3 className="font-semibold text-[#0F172A]">Staff Login</h3>
              <p className="text-sm text-[#64748B]">Upload production drawings for client approval</p>
            </div>
          </div>
        </div>
      </section>

      {/* Login/Register Form */}
      <section className="py-16">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#E2E8F0] p-8"
          >
            {view === "registered" ? (
              /* Registration Success - Pending Approval */
              <div className="text-center" data-testid="registration-success">
                <div className="w-16 h-16 bg-[#F0FDF4] border-2 border-green-400 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Mail className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-[#0F172A] mb-3">Registration Submitted!</h2>
                <div className="bg-[#FFFBEB] border border-[#FCD34D] p-4 text-left mb-5">
                  <p className="text-sm text-[#92400E] leading-relaxed">
                    You will be getting a Registration confirmation mail from <strong>hello@ezeefitofficesystems.in</strong> or alternatively you may try logging in after 1 hour.
                  </p>
                </div>
                <p className="text-sm text-[#64748B] mb-6">
                  Our team is reviewing your registration. Once approved, you will receive a welcome email with login access to the Client Portal.
                </p>
                <button
                  onClick={() => { setView("login"); setLoginData({ email: registerData.email, password: "" }); }}
                  className="w-full bg-[#0F172A] text-white py-3 font-semibold hover:bg-[#1E293B] transition-colors"
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </button>
              </div>
            ) : view === "2fa" ? (
              /* Two-Factor Authentication View — Email OTP */
              <div data-testid="2fa-view">
                <button
                  onClick={() => { setView("login"); setTwoFaTempToken(""); setOtpCode(""); setTwoFaMaskedEmail(""); }}
                  className="flex items-center text-sm text-[#64748B] hover:text-[#0F172A] mb-6"
                  data-testid="2fa-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </button>

                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-[#FFF7ED] border-2 border-[#F97316] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-[#F97316]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#0F172A] mb-1">2-Step Verification</h2>
                  <p className="text-sm text-[#64748B]">
                    We&apos;ve emailed a 6-digit verification code to <strong className="text-[#0F172A]">{twoFaMaskedEmail}</strong>.
                  </p>
                  <p className="text-xs text-[#94A3B8] mt-1">Code expires in 10 minutes.</p>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#334155] mb-2">
                      Verification Code
                    </label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      autoFocus
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="h-14 rounded-none text-center text-2xl font-mono tracking-[0.5em]"
                      data-testid="2fa-otp-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading || otpCode.length !== 6}
                    className="w-full h-12 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                    data-testid="2fa-verify-btn"
                  >
                    {isLoading ? "Verifying..." : "Verify & Login"}
                  </Button>
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resending}
                      className="text-sm text-[#F97316] hover:text-orange-600 disabled:opacity-50"
                      data-testid="2fa-resend-btn"
                    >
                      {resending ? "Sending…" : "Didn't get the code? Resend"}
                    </button>
                  </div>
                </form>
              </div>
            ) : view === "forgot" ? (
              /* Forgot Password View — Email OTP flow */
              <div>
                <button
                  onClick={() => { setView("login"); setForgotStep("request"); setForgotEmail(""); setForgotOtp(""); setForgotNewPwd(""); }}
                  className="flex items-center text-sm text-[#64748B] hover:text-[#0F172A] mb-6"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </button>

                <h2 className="text-xl font-bold text-[#0F172A] mb-2">Reset Your Password</h2>
                <p className="text-sm text-[#64748B] mb-6">
                  {forgotStep === "request"
                    ? "Enter your registered email and we'll send you a 6-digit OTP."
                    : "Check your inbox for the OTP, then choose a new password below."}
                </p>

                {forgotStep === "request" ? (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="h-12 rounded-none"
                        data-testid="forgot-email"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                      data-testid="forgot-submit"
                    >
                      {isLoading ? "Sending OTP..." : "Send OTP"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPasswordWithOtp} className="space-y-4" data-testid="reset-with-otp-form">
                    <div className="bg-[#F1F5F9] border border-[#E2E8F0] p-3 text-xs text-[#64748B]">
                      OTP sent to <strong className="text-[#0F172A]">{forgotEmail}</strong>. Expires in 10 minutes.
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">6-Digit OTP</label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        required
                        autoFocus
                        value={forgotOtp}
                        onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000"
                        className="h-14 rounded-none text-center text-2xl font-mono tracking-[0.5em]"
                        data-testid="forgot-otp-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">New Password</label>
                      <div className="relative">
                        <Input
                          type={showForgotPwd ? "text" : "password"}
                          required
                          value={forgotNewPwd}
                          onChange={(e) => setForgotNewPwd(e.target.value)}
                          placeholder="Min 8 chars, letter + number + symbol"
                          className="h-12 rounded-none pr-10"
                          data-testid="forgot-new-pwd"
                        />
                        <button type="button" onClick={() => setShowForgotPwd(!showForgotPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                          {showForgotPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {forgotNewPwd && (
                        <ul className="mt-2 space-y-1">
                          {PASSWORD_RULES.map((rule, i) => {
                            const ok = rule.test(forgotNewPwd);
                            return (
                              <li key={i} className={`flex items-center gap-1.5 text-xs ${ok ? "text-green-600" : "text-[#64748B]"}`}>
                                {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {rule.label}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading || forgotOtp.length !== 6}
                      className="w-full h-12 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                      data-testid="forgot-reset-submit"
                    >
                      {isLoading ? "Resetting..." : "Reset Password"}
                    </Button>
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={() => { setForgotStep("request"); setForgotOtp(""); setForgotNewPwd(""); }}
                        className="text-sm text-[#F97316] hover:text-orange-600"
                        data-testid="forgot-back-to-email"
                      >
                        Use a different email / resend OTP
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              /* Login / Register View */
              <>
                {/* Tabs */}
                <div className="flex mb-8">
                  <button
                    onClick={() => setView("login")}
                    className={`flex-1 py-3 text-center font-semibold transition-colors ${
                      view === "login"
                        ? "bg-[#0F172A] text-white"
                        : "bg-[#F8FAFC] text-[#64748B] hover:text-[#0F172A]"
                    }`}
                  >
                    <LogIn className="w-4 h-4 inline mr-2" />
                    Login
                  </button>
                  <button
                    onClick={() => setView("register")}
                    className={`flex-1 py-3 text-center font-semibold transition-colors ${
                      view === "register"
                        ? "bg-[#0F172A] text-white"
                        : "bg-[#F8FAFC] text-[#64748B] hover:text-[#0F172A]"
                    }`}
                  >
                    <UserPlus className="w-4 h-4 inline mr-2" />
                    Register
                  </button>
                </div>

                {view === "login" ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Email / Gmail ID
                      </label>
                      <Input
                        type="email"
                        required
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        placeholder="your@gmail.com"
                        className="h-12 rounded-none"
                        data-testid="login-email"
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Password
                      </label>
                      <div className="relative">
                      <Input
                        type={showLoginPwd ? "text" : "password"}
                        required
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="••••••••"
                        className="h-12 rounded-none pr-11"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowLoginPwd(!showLoginPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155] transition-colors"
                        data-testid="toggle-login-password"
                      >
                        {showLoginPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      </div>
                    </div>
                    
                    {/* Forgot Password Link */}
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setView("forgot")}
                        className="text-sm text-[#F97316] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                      data-testid="login-submit"
                    >
                      {isLoading ? "Logging in..." : "Login"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Full Name *
                      </label>
                      <Input
                        required
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        placeholder="Your full name"
                        className="h-12 rounded-none"
                        data-testid="register-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Company Name *
                      </label>
                      <Input
                        required
                        value={registerData.company_name}
                        onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                        placeholder="Your company"
                        className="h-12 rounded-none"
                        data-testid="register-company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Gmail ID / Email *
                      </label>
                      <Input
                        type="email"
                        required
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        placeholder="your@gmail.com"
                        className="h-12 rounded-none"
                        data-testid="register-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Phone *
                      </label>
                      <Input
                        type="tel"
                        required
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        placeholder="9820042100"
                        className="h-12 rounded-none"
                        data-testid="register-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Create Password *
                      </label>
                      <div className="relative">
                      <Input
                        type={showRegPwd ? "text" : "password"}
                        required
                        minLength={8}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        placeholder="Min 8 characters"
                        className="h-12 rounded-none pr-11"
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowRegPwd(!showRegPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155] transition-colors"
                        data-testid="toggle-register-password"
                      >
                        {showRegPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      </div>
                      {registerData.password.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {PASSWORD_RULES.map((rule) => {
                            const passed = rule.test(registerData.password);
                            return (
                              <div key={rule.label} className="flex items-center gap-1.5 text-xs">
                                {passed ? (
                                  <Check className="w-3.5 h-3.5 text-green-500" />
                                ) : (
                                  <X className="w-3.5 h-3.5 text-red-400" />
                                )}
                                <span className={passed ? "text-green-600" : "text-[#94A3B8]"}>{rule.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                      <Input
                        type={showRegConfirmPwd ? "text" : "password"}
                        required
                        minLength={8}
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        placeholder="Re-enter password"
                        className="h-12 rounded-none pr-11"
                        data-testid="register-confirm-password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowRegConfirmPwd(!showRegConfirmPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155] transition-colors"
                        data-testid="toggle-register-confirm-password"
                      >
                        {showRegConfirmPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      </div>
                      {registerData.confirmPassword.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs mt-1.5">
                          {registerData.password === registerData.confirmPassword ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-green-600">Passwords match</span>
                            </>
                          ) : (
                            <>
                              <X className="w-3.5 h-3.5 text-red-400" />
                              <span className="text-[#94A3B8]">Passwords do not match</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                      data-testid="register-submit"
                    >
                      {isLoading ? "Registering..." : "Register as Client"}
                    </Button>
                  </form>
                )}
              </>
            )}

            {view !== "forgot" && (
              <p className="text-sm text-[#64748B] mt-6 text-center">
                Need help?{" "}
                <Link to="/contact" className="text-[#F97316] hover:underline">
                  Contact support
                </Link>
              </p>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
